/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLock} from "@fortawesome/pro-regular-svg-icons";
import {betterMemo} from "js/common/utils/more-memo";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import TooltipLabel from "js/common/views/tooltip-label";
import {TextButton} from "js/common/views/inputs/buttons";
import Tooltip from "@mui/material/Tooltip";
import {getGroupingEntity, getQueryType} from "js/admin/kpis/edit-kpis/utils";
import Immutable from "immutable";
import currentClient from "js/common/repo/backbone/current-client";
import * as Permissions from "js/common/permissions";
import * as Users from "js/common/users";

const customComponents = {
  Option: ({innerRef, innerProps, isFocused, children, data}) => {
    const {theme} = React.useContext(CustomThemeContext);
    return (
        <div
            ref={innerRef}
            {...innerProps}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 12px",
              fontSize: 13,
              cursor: "pointer",
              color: theme.palette.text.main,
              backgroundColor: isFocused ? theme.palette.background.paper : theme.palette.background.card
            }}>
          <span>{children}</span>
          {data.kpi?.systemDefault &&
              <Tooltip title="This is a system default metric. To make changes, duplicate it and edit the copy." placement="left">
                <span style={{marginLeft: 8, flexShrink: 0, display: "flex", alignItems: "center"}}>
                  <FontAwesomeIcon icon={faLock} style={{color: theme.palette.primary.main}} />
                </span>
              </Tooltip>}
        </div>
    );
  },

  Group: (props) => {
    const {children, label, data} = props;
    const [expanded, setExpanded] = React.useState(
        data.defaultExpanded !== undefined ? data.defaultExpanded : true
    );
    const {theme} = React.useContext(CustomThemeContext);
    const isSearching = data.isSearching;

    return (
        <div key={`group-${label.replace(" ", "-")}`}>
          <div>
            <div
                style={{
                  display: "flex",
                  padding: "5px 10px",
                  fontSize: 13,
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  fontWeight: 500,
                  fontFamily: theme.typography.fontFamilyBold,
                  cursor: "pointer"
                }}
                onClick={() => !isSearching && setExpanded(!expanded)}>
              {label}
              {!isSearching && <i className={`bhi-${expanded ? "sort-asc" : "sort-desc"}`} />}
            </div>
          </div>
          <div style={{display: expanded || isSearching ? "block" : "none"}}>{children}</div>
        </div>
    );
  },
  
  Menu: (props) => {
    const {theme} = React.useContext(CustomThemeContext);
    const {children, innerProps} = props;

    const currentUser = Users.getCurrentUser();
    const [userIsKpiEditor, isCube19User, clientHasEdit] = React.useMemo(() => {
      return [
        Users.currentHasPermission(Permissions.userPermissions.canSubmitKpis),
        Users.isCube19User(currentUser),
        currentClient.hasPermission(Permissions.clientPermissions.canEditKpiConfigs)];
    }, [currentUser]);

    const addNewButtonStyle = css`
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 10px;
        background-color: ${theme.palette.primary.main};
        color: ${theme.palette.text.inverted};
        cursor: pointer;
        width: 380px;
    `;

    return (
        <div
            {...innerProps}
            style={{
              ...innerProps.style,
              backgroundColor: theme.palette.background.card,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              borderRadius: 4,
              paddingBottom: 4,
              width: props.selectProps.width
            }}
        >
          {children}
          {(clientHasEdit || (userIsKpiEditor && isCube19User)) && <TextButton
              testId="metric-drawer-create-metric"
              type="primary"
              css={addNewButtonStyle}
              label="Add new metric"
              onClick={() => props.selectProps.onAddNewMetric()} />}
        </div>
    );
  }
};

const KpiByEntityPicker = betterMemo(
    {displayName: "KpiByEntityPicker"},
    ({
      wrappedKpis,
      idToTemplate,
      entityTypeToLabel,
      onKpiSelect,
      selectedKpiId,
      label,
      labelTooltip,
      disabled,
      placeholder,
      width,
      onAddMetricClick,
      menuIsOpen,
      onMenuClose
    }) => {
      const {theme} = React.useContext(CustomThemeContext);
      const [isSearching, setIsSearching] = React.useState(false);

      const styles = {
        placeholder: (provided, state) => ({
          ...provided,
          fontSize: 14,
          color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary
        }),
        input: (provided) => ({
          ...provided,
          color: theme.palette.text.main,
          fontFamily: `${theme.typography.fontFamily} !important`,
          input: {
            fontFamily: `${theme.typography.fontFamily} !important`,
            color: theme.palette.text.main
          }
        }),
        control: (provided, state) => ({
          ...provided,
          backgroundColor: "transparent",
          opacity: state.isDisabled ? 0.5 : 1,
          border: "none",
          borderBottom: `2px solid ${theme.palette.background.paper}`,
          boxShadow: "none",
          minHeight: 0,
          marginTop: 4,
          marginBottom: 3,
          cursor: "pointer",
          fontSize: 14,
          ":hover": {
            borderBottom: `2px solid ${theme.palette.background.paper}`
          }
        }),
        dropdownIndicator: (provided, state) => ({
          ...provided,
          display: "none",
          color: state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary,
          ":hover": state.isDisabled ? theme.palette.text.disabled : theme.palette.text.primary
        }),
        indicatorSeparator: () => ({display: "none"}),
        menuPortal: (base) => ({...base, zIndex: 999999, position: "fixed"}),
        menuList: (provided) => ({
          ...provided,
          overflowX: "hidden"
        }),
        option: (provided, state) => ({
          ...provided,
          color: theme.palette.text.main,
          fontSize: 13,
          backgroundColor: state.isFocused
              ? theme.palette.background.paper
              : theme.palette.background.card
        }),
        singleValue: (provided, state) => ({
          ...provided,
          fontSize: 13,
          color: `${state.isDisabled
              ? theme.palette.text.disabled
              : theme.palette.text.primary} !important`,
          padding: "3px 0"
        })
      };

      const groupedOptions = React.useMemo(() => {
        if (!wrappedKpis || !wrappedKpis.size) {
          return [];
        }
        const entityTypeToWrappedKpis = wrappedKpis.groupBy(wk => getGroupingEntity(wk));
        return entityTypeToLabel
            .delete("PLACEMENT_SPLIT")
            .delete("USER")
            .entrySeq()
            .sort(([_1, label1], [_2, label2]) => label1.localeCompare(label2))
            .map(([entityType, entityLabel]) => {
              return [entityLabel, entityTypeToWrappedKpis.get(entityType, Immutable.List())];
            })
            .concat([
              [
                "Simple Sum", wrappedKpis
                  .filter(wrappedKpi => !getGroupingEntity(wrappedKpi) &&
                      getQueryType(wrappedKpi.get("kpi"), idToTemplate) === "SIMPLE_SUM")],
              [
                "Other", wrappedKpis
                  .filter(wrappedKpi => !getGroupingEntity(wrappedKpi) &&
                      getQueryType(wrappedKpi.get("kpi"), idToTemplate) !== "SIMPLE_SUM")]])
            .map(([entityLabel, kpisForEntity]) => {
              return {
                label: entityLabel,
                options: kpisForEntity
                    .sortBy(kpi => kpi.getIn(["kpi", "name"]))
                    .filter(kpi => !kpi.getIn(["kpi", "deleted"], false) && !kpi.getIn(["kpi", "hidden"], false))
                    .map(kpi => ({
                      value: kpi.getIn(["kpi", "id"]),
                      label: kpi.getIn(["kpi", "name"]),
                      originalName: kpi.getIn(["kpi", "trueName"]),
                      entityLabel: entityLabel,
                      kpi: kpi.get("kpi").toJS()
                    }))
                    .toJS(),
                defaultExpanded: false,
                isSearching: isSearching
              };
            })
            .toJS();
      }, [wrappedKpis, entityTypeToLabel, isSearching]);

      // Get the selected option
      const selectedOption = React.useMemo(() => {
        if (!selectedKpiId || !groupedOptions.length) {
          return null;
        }

        // Flatten all options from all groups
        const allOptions = [];
        groupedOptions.forEach(group => {
          if (Array.isArray(group.options)) {
            allOptions.push(...group.options);
          }
        });

        return allOptions.find(option => option.value === selectedKpiId) || null;
      }, [groupedOptions, selectedKpiId]);

      const filterOption = (option, searchText) => {
        if (!searchText) {
          return true;
        }
        const searchWords = searchText.toLowerCase().split(" ");
        const searchableText = [
          String(option.data?.kpi?.id ?? ""),
          option.label ?? "",
          option.data?.originalName ?? "",
          option.data?.entityLabel ?? ""
        ].join(" ").toLowerCase();

        return searchWords.every(word => searchableText.includes(word));
      };

      const handleSearch = (text) => {
        setIsSearching(text.length > 0);
        return text;
      };

      return (
          <>
            {label && <TooltipLabel label={label} tooltip={labelTooltip} />}
            <Select
                autoFocus
                menuIsOpen={menuIsOpen}
                onMenuClose={onMenuClose}
                menuPortalTarget={document.body.getElementsByClassName("MuiDrawer-root")[0]}
                menuPosition="fixed"
                searchable
                options={groupedOptions}
                onInputChange={handleSearch}
                blurInputOnSelect={false}
                closeMenuOnSelect={true}
                filterOption={filterOption}
                components={customComponents}
                styles={styles}
                value={selectedOption || ""}
                onChange={(option) => onKpiSelect(option.value, option.data?.kpi)}
                isDisabled={disabled}
                placeholder={placeholder || "Select a metric..."}
                onAddNewMetric={() => onAddMetricClick()}
                width={width}
            />
          </>
      );
    }
);

export default KpiByEntityPicker;